import { A2uiComponentEntry } from 'types/space';

/**
 * A component's "children" are not a fixed prop name at this layer — the catalog defines per-type
 * shapes (Column.children, Tabs.tabs[].children, List.template, ...). Generically: any prop value
 * that is itself a string matching another component's id, or an array/object containing such
 * strings, is an edge in the component tree. This lets `validateSpaceDocument` walk depth and
 * detect cycles without depending on `@center-platform/component-catalog`.
 */
export function findChildIds(entry: A2uiComponentEntry, knownIds: ReadonlySet<string>): string[] {
  const found = new Set<string>();

  const visit = (value: unknown): void => {
    if (typeof value === 'string') {
      if (value !== entry.id && knownIds.has(value)) {
        found.add(value);
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value).forEach(visit);
    }
  };

  Object.entries(entry).forEach(([key, value]) => {
    if (key === 'id' || key === 'component') return;
    visit(value);
  });

  return Array.from(found);
}

export interface ComponentGraphIssue {
  type: 'CYCLE' | 'DEPTH_EXCEEDED';
  /** Component ids forming the cycle (in order), or the path exceeding the depth limit. */
  path: string[];
}

/**
 * Walks the component tree from `root`, following generic child-id references, detecting cycles
 * and reporting the first path that exceeds `maxDepth`. Fail-closed: stops at the first issue.
 */
export function walkComponentGraph(
  components: A2uiComponentEntry[],
  root: string,
  maxDepth: number
): ComponentGraphIssue | null {
  const byId = new Map(components.map((entry) => [entry.id, entry]));
  const knownIds = new Set(byId.keys());

  const visit = (id: string, path: string[]): ComponentGraphIssue | null => {
    if (path.includes(id)) {
      return { type: 'CYCLE', path: [...path, id] };
    }

    const nextPath = [...path, id];
    if (nextPath.length > maxDepth + 1) {
      return { type: 'DEPTH_EXCEEDED', path: nextPath };
    }

    const entry = byId.get(id);
    if (!entry) return null;

    for (const childId of findChildIds(entry, knownIds)) {
      const issue = visit(childId, nextPath);
      if (issue) return issue;
    }

    return null;
  };

  return visit(root, []);
}

/**
 * Actual max depth of the component tree from `root` (1 = just the root), for display in a
 * limits meter — unlike `walkComponentGraph`, which stops at the FIRST depth-exceeding or
 * cyclic path, this measures the whole tree. A cycle is truncated (never infinite) by refusing
 * to revisit an id already on the current path, so the cyclic branch just contributes its
 * path-so-far length rather than aborting the measurement.
 */
export function computeComponentTreeDepth(components: A2uiComponentEntry[], root: string): number {
  const byId = new Map(components.map((entry) => [entry.id, entry]));
  const knownIds = new Set(byId.keys());
  if (!byId.has(root)) return 0;

  const visit = (id: string, path: string[]): number => {
    if (path.includes(id)) return path.length;

    const nextPath = [...path, id];
    const entry = byId.get(id);
    if (!entry) return nextPath.length;

    const childDepths = findChildIds(entry, knownIds).map((childId) => visit(childId, nextPath));
    return childDepths.length > 0 ? Math.max(...childDepths) : nextPath.length;
  };

  return visit(root, []);
}
